import React from 'react'

import {
  Media,
  type Glyph,
  GlyphMediaVariant,
} from '@tribeplatform/gql-client/types'
import { useImageUploader } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'

import { useI18n } from '../i18n/providers/I18nProvider.js'
import { emojiToImageId, iconToImageId } from '../Picker/index.js'
import { PopoverImagePicker } from '../Picker/PopoverImagePicker.js'
import type { PickerEmoji, PickerIcon } from '../Picker/types.js'
import { handleUploadFileError } from '../utils/error.js'

export const ImagePickerBlockField = ({
  onChange,
  removable,
  tabs = ['emoji', 'icon', 'upload', 'link', 'unsplash'],
  hasImage,
}: {
  removable?: boolean
  hasImage: boolean
  tabs?: ('emoji' | 'icon' | 'upload' | 'link' | 'unsplash')[]
  onChange: (media: Media | undefined) => void
}) => {
  const intl = useI18n()
  const { $t } = intl
  const { uploadImage, isLoading } = useImageUploader()

  const uploadFile = async (files: Array<File>) => {
    const file = files.at(0)
    try {
      const uploadedImage = await uploadImage(file)
      onChange(uploadedImage)
    } catch (error) {
      handleUploadFileError(intl)(error)
    }
  }

  const onEmojiChange = async (pickerEmoji: PickerEmoji) => {
    const glyph: Glyph = {
      id: emojiToImageId(pickerEmoji),
      text: pickerEmoji.unified,
      variant: GlyphMediaVariant.emoji,
    }
    onChange(glyph)
  }

  const onIconChange = async (pickerIcon: PickerIcon) => {
    const glyph: Glyph = {
      id: iconToImageId(pickerIcon),
      text: pickerIcon.name,
      variant: GlyphMediaVariant.icon,
    }
    onChange(glyph)
  }

  return (
    <PopoverImagePicker
      tabs={tabs}
      removable={removable && !!hasImage}
      onRemove={() => {
        onChange(null)
      }}
      onEmojiChange={onEmojiChange}
      onImageChange={uploadFile}
      onIconChange={onIconChange}
      sizeConfig={{
        recommendedWidth: 1200,
        recommendedHeight: 440,
      }}
    >
      <Button
        variant="secondaryNeutral"
        loading={isLoading}
        disabled={isLoading}
      >
        {hasImage
          ? $t({
              id: 'Generics.Change.Verb',
              defaultMessage: 'Change',
            })
          : $t({ id: 'Generics.Add', defaultMessage: 'Add' })}
      </Button>
    </PopoverImagePicker>
  )
}
